import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  Wrench, Settings, Palette, Target, Crosshair, RefreshCw, ClipboardCheck,
  Shield, Flag, Users, Award
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroSection from '../components/home/HeroSection';
import ServicesSection from '../components/home/ServicesSection';
import GallerySection from '../components/home/GallerySection';
import PricingSection from '../components/home/PricingSection';
import StorySection from '../components/home/StorySection';
import ValuesSection from '../components/home/ValuesSection';
import ContactSection from '../components/home/ContactSection';
import PageRenderer from '../components/builder/PageRenderer';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { services, values, pricingCategories as fallbackPricing, galleryItems } from '../mock';
import { TOKEN_KEY } from '../components/admin/adminApi';
import { logWarn } from '../lib/logger';

const iconMap = {
  'wrench': Wrench, 'settings': Settings, 'palette': Palette, 'target': Target,
  'crosshair': Crosshair, 'refresh-cw': RefreshCw, 'clipboard-check': ClipboardCheck,
  'shield': Shield, 'flag': Flag, 'users': Users, 'award': Award,
};

const API = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [pricingCategories, setPricingCategories] = useState(fallbackPricing);
  const [builderPage, setBuilderPage] = useState(undefined); // undefined = loading, null = none
  const { homepage } = useSiteSettings();
  const location = useLocation();
  const isPreview = new URLSearchParams(location.search).get('preview') === '1';

  // Load the builder page config (published, or draft when ?preview=1 with a valid token).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isPreview) {
          const token = localStorage.getItem(TOKEN_KEY);
          const { data } = await axios.get(`${API}/api/admin/page-builder/home`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!cancelled) setBuilderPage(data?.draft?.sections ? data.draft : null);
        } else {
          const { data } = await axios.get(`${API}/api/page-builder/home`);
          if (!cancelled) setBuilderPage(data?.sections ? data : null);
        }
      } catch (err) {
        logWarn('Page builder config unavailable, using legacy layout:', err?.message || err);
        if (!cancelled) setBuilderPage(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isPreview]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/pricing`);
        if (!cancelled && Array.isArray(data) && data.length > 0) setPricingCategories(data);
      } catch (err) {
        logWarn('Pricing API unavailable, using fallback:', err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Legacy fallback ordering (only used if there is no builder config at all).
  const orderedSections = useMemo(() => {
    const configured = (homepage?.sections || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
    if (configured.length) return configured;
    return [
      { key: 'hero', visible: true }, { key: 'services', visible: true },
      { key: 'gallery_preview', visible: true }, { key: 'pricing', visible: true },
      { key: 'story', visible: true }, { key: 'values', visible: true },
      { key: 'contact', visible: true },
    ];
  }, [homepage]);

  const renderLegacy = (key) => {
    switch (key) {
      case 'hero': return <HeroSection key="hero" quoteDialogOpen={quoteDialogOpen} setQuoteDialogOpen={setQuoteDialogOpen} />;
      case 'services': return <ServicesSection key="services" services={services} fullIconMap={iconMap} />;
      case 'gallery_preview':
      case 'gallery': return <GallerySection key="gallery" galleryItems={galleryItems} />;
      case 'pricing': return <PricingSection key="pricing" pricingCategories={pricingCategories} quoteDialogOpen={quoteDialogOpen} setQuoteDialogOpen={setQuoteDialogOpen} />;
      case 'story': return <StorySection key="story" />;
      case 'values': return <ValuesSection key="values" values={values} iconMap={iconMap} />;
      case 'contact': return <ContactSection key="contact" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      {isPreview && (
        <div className="bg-red-600 text-white text-center text-sm py-2 font-semibold" data-testid="preview-banner">
          Preview mode — this is your unpublished draft. Publish from the editor to make it live.
        </div>
      )}
      {builderPage === undefined ? (
        <div className="min-h-[60vh]" />
      ) : builderPage ? (
        <PageRenderer page={builderPage} quoteDialogOpen={quoteDialogOpen} setQuoteDialogOpen={setQuoteDialogOpen} />
      ) : (
        orderedSections.map((s) => (s.visible ? renderLegacy(s.key) : null))
      )}
      <Footer />
    </div>
  );
};

export default Home;
